import React, { useMemo, useState } from 'react';
import { API_BASE } from '../apiBase';

interface ProductImage {
  url: string;
  alt: string;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  images: ProductImage[];
  variants: Array<{
    id: string;
    title: string;
    price: string;
    image?: ProductImage;
  }>;
  tags: string[];
  productType: string;
  vendor: string;
  metafields?: Record<string, any>;
}

interface PromptDetail {
  promptId: string;
  promptText: string;
  visualGoal?: string;
  differentiation?: string;
  cameraDirection?: string;
  colorPalette?: string;
  emotionalTone?: string;
  supportingNarration?: string;
}

interface PromptSegment {
  segmentOrder: number;
  segmentName?: string;
  narrationExcerpt?: string;
  keyMoments?: string[];
  contentCategory?: string;
  prompts: PromptDetail[];
}

interface PromptResponse {
  segments: PromptSegment[];
  globalStyleGuide?: Record<string, string>;
  narrationNotes?: string;
}

type SelectionState = Record<number, string[]>;

type SlideExport = {
  title: string;
  slides: Array<{
    layout: string;
    spokenCue: string;
    title?: string | null;
    points?: string[] | null;
    useProductImage: boolean;
    imagePrompt: string;
    contentType: string;
  }>;
  styleGuide?: Record<string, string>;
  narrationNotes?: string;
};

const MAX_PROMPT_LIMIT = 200;

interface VideoPromptGeneratorProps {
  embedded?: boolean;
  initialScript?: string;
  initialTitle?: string;
}

const VideoPromptGenerator: React.FC<VideoPromptGeneratorProps> = ({ embedded = false, initialScript = '', initialTitle = '' }) => {
  const [scriptTitle, setScriptTitle] = useState(initialTitle);
  const [scriptInput, setScriptInput] = useState(initialScript);
  const [promptsPerSegment, setPromptsPerSegment] = useState<number>(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PromptResponse | null>(null);
  const [selectedPrompts, setSelectedPrompts] = useState<SelectionState>({});
  const [lastGeneratedAt, setLastGeneratedAt] = useState<number | null>(null);
  const [segmentProducts, setSegmentProducts] = useState<Record<number, Product[]>>({});

  // Product search state
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [loadingProducts, setLoadingProducts] = useState(false);

  const searchProducts = async () => {
    if (!searchQuery.trim()) return;

    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_BASE}/api/search-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, rawInput: searchQuery }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to search products');

      const data = await response.json();
      setProducts(data.products || []);
      setSelectedProducts(new Set());
    } catch (error) {
      console.error('Search error:', error);
      alert('Failed to search products. Check console for details.');
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProductSelection = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const assignProductsToSegments = () => {
    if (!result || selectedProducts.size === 0) return;

    const selectedProductData = products.filter(p => selectedProducts.has(p.id));
    const segmentsWithProducts = result.segments.length;
    const productsPerSegment = Math.floor(selectedProductData.length / segmentsWithProducts);
    let remainder = selectedProductData.length % segmentsWithProducts;

    const newSegmentProducts: Record<number, Product[]> = {};

    let productIndex = 0;
    result.segments.forEach((segment) => {
      const numProducts = productsPerSegment + (remainder > 0 ? 1 : 0);
      remainder--;

      newSegmentProducts[segment.segmentOrder] = selectedProductData.slice(productIndex, productIndex + numProducts);
      productIndex += numProducts;
    });

    setSegmentProducts(newSegmentProducts);
  };

  const assignProductToSegment = (productId: string, segmentOrder: number) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    setSegmentProducts((prev) => ({
      ...prev,
      [segmentOrder]: [...(prev[segmentOrder] || []), product]
    }));

    // Remove from selected products
    setSelectedProducts((prev) => {
      const newSelected = new Set(prev);
      newSelected.delete(productId);
      return newSelected;
    });
  };

  const removeProductFromSegment = (segmentOrder: number, productId: string) => {
    const product = segmentProducts[segmentOrder]?.find(p => p.id === productId);
    if (!product) return;

    setSegmentProducts((prev) => ({
      ...prev,
      [segmentOrder]: (prev[segmentOrder] || []).filter(p => p.id !== productId)
    }));

    // Add back to selected products
    setSelectedProducts((prev) => new Set([...prev, productId]));
  };

  const generateWithSegmentProducts = async () => {
    if (!result) return;
    setLoading(true);
    setError(null);
    try {
      const productAssignments = result.segments.map((segment) => ({
        segmentOrder: segment.segmentOrder,
        segmentName: segment.segmentName,
        products: (segmentProducts[segment.segmentOrder] || []).map((p) => ({
          name: p.title,
          placementNote: `Featured in ${segment.segmentName}`
        }))
      }));

      const response = await fetch(`${API_BASE}/api/generate-video-prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          script: scriptInput,
          scriptTitle: scriptTitle || undefined,
          promptsPerSegment,
          productAssignments
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || 'Failed to regenerate prompts');
      }

      const data: PromptResponse = await response.json();
      setResult(data);
      const defaults: SelectionState = {};
      data.segments.forEach((segment) => {
        defaults[segment.segmentOrder] = segment.prompts.map((prompt) => prompt.promptId);
      });
      setSelectedPrompts(defaults);
      setLastGeneratedAt(Date.now());
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!scriptInput.trim()) {
      setError('Script text is required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/generate-video-prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          script: scriptInput,
          scriptTitle: scriptTitle || undefined,
          promptsPerSegment,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || 'Failed to generate prompts');
      }
      const data: PromptResponse = await response.json();
      setResult(data);
      const defaults: SelectionState = {};
      data.segments.forEach((segment) => {
        defaults[segment.segmentOrder] = segment.prompts.map((prompt) => prompt.promptId);
      });
      setSelectedPrompts(defaults);
      setLastGeneratedAt(Date.now());
    } catch (err: any) {
      setError(err.message || 'Failed to generate prompts');
    } finally {
      setLoading(false);
    }
  };

  const handlePromptPerSegmentChange = (value: number) => {
    if (Number.isNaN(value) || value < 1) {
      setPromptsPerSegment(1);
      return;
    }
    const clamped = Math.min(Math.round(value), MAX_PROMPT_LIMIT);
    setPromptsPerSegment(clamped);
  };

  const togglePromptSelection = (segmentOrder: number, promptId: string) => {
    setSelectedPrompts((prev) => {
      const current = prev[segmentOrder] || [];
      const exists = current.includes(promptId);
      const next = exists ? current.filter((id) => id !== promptId) : [...current, promptId];
      return { ...prev, [segmentOrder]: next };
    });
  };

  const bulkSelect = (mode: 'all' | 'none') => {
    if (!result) return;
    const next: SelectionState = {};
    result.segments.forEach((segment) => {
      next[segment.segmentOrder] = mode === 'all' ? segment.prompts.map((prompt) => prompt.promptId) : [];
    });
    setSelectedPrompts(next);
  };

  const selectedPromptCount = useMemo(() => {
    if (!result) return 0;
    return result.segments.reduce((total, segment) => {
      const chosen = selectedPrompts[segment.segmentOrder] || [];
      return total + chosen.length;
    }, 0);
  }, [result, selectedPrompts]);

  const totalPromptCount = useMemo(() => {
    if (!result) return 0;
    return result.segments.reduce((total, segment) => total + segment.prompts.length, 0);
  }, [result]);

  const buildExportPayload = useMemo<SlideExport | null>(() => {
    if (!result) return null;
    const slides: SlideExport['slides'] = [];
    result.segments.forEach((segment) => {
      const chosen = selectedPrompts[segment.segmentOrder] || [];
      segment.prompts
        .filter((prompt) => chosen.includes(prompt.promptId))
        .forEach((prompt) => {
          const contentType = segment.contentCategory || 'explanation';
          const titleValue = ['segment_title', 'steps', 'emphasis'].includes(contentType)
            ? segment.segmentName || null
            : null;
          const pointsValue = segment.keyMoments && segment.keyMoments.length > 0 ? segment.keyMoments.slice(0, 4) : null;
          slides.push({
            layout: 'full-slides',
            spokenCue: prompt.supportingNarration || segment.narrationExcerpt || '',
            title: titleValue,
            points: pointsValue,
            useProductImage: true,
            imagePrompt: prompt.promptText,
            contentType,
          });
        });
    });
    return {
      title: scriptTitle.trim() || 'Video Visual Plan',
      slides,
      styleGuide: result.globalStyleGuide,
      narrationNotes: result.narrationNotes,
    };
  }, [result, scriptTitle, selectedPrompts]);

  const exportJsonString = useMemo(() => {
    if (!buildExportPayload) return '';
    return JSON.stringify(buildExportPayload, null, 2);
  }, [buildExportPayload]);

  const copyAllPrompts = async () => {
    if (!result) return;
    const lines: string[] = [];
    result.segments.forEach((segment) => {
      const chosen = selectedPrompts[segment.segmentOrder] || [];
      const header = segment.segmentName || `Segment ${segment.segmentOrder}`;
      lines.push(`${header}`);
      segment.prompts
        .filter((prompt) => chosen.includes(prompt.promptId))
        .forEach((prompt, index) => {
          lines.push(`  ${index + 1}. ${prompt.promptText}`);
        });
      lines.push('');
    });
    await navigator.clipboard.writeText(lines.join('\n'));
  };

  const copyJson = async () => {
    if (!exportJsonString) return;
    await navigator.clipboard.writeText(exportJsonString);
  };

  const downloadJson = () => {
    if (!exportJsonString) return;
    const blob = new Blob([exportJsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-visual-plan-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const containerClasses = embedded ? 'space-y-6' : 'max-w-7xl mx-auto p-6 space-y-6';
  const cardClasses = embedded
    ? 'rounded-xl border border-slate-200 bg-white p-6 space-y-6'
    : 'bg-white rounded-lg shadow-md p-6 space-y-6 border border-slate-200';

  return (
    <div className={containerClasses}>
      <div className={cardClasses}>
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">🎬 Video Prompt Planner</h2>
          <p className="text-slate-600">
            Transform your narration script into segment-based visual prompts featuring neutral, timeless interior design styles (mid-century, farmhouse, rustic, wabi-sabi, Japanese, minimalist, Scandinavian). Perfect for sourcing or generating images that complement your content.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Script Title (optional)</label>
              <input
                type="text"
                value={scriptTitle}
                onChange={(e) => setScriptTitle(e.target.value)}
                placeholder="Interior Design Narration"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="prompts-per-segment-input">Prompts per Segment</label>
              <div className="flex items-center gap-3">
                <input
                  id="prompts-per-segment-input"
                  type="number"
                  min={1}
                  max={MAX_PROMPT_LIMIT}
                  value={promptsPerSegment}
                  onChange={(e) => handlePromptPerSegmentChange(Number(e.target.value))}
                  className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <span className="text-xs text-slate-500">Enter any whole number between 1 and {MAX_PROMPT_LIMIT}.</span>
              </div>
            </div>
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white font-semibold rounded-lg px-6 py-3 hover:from-fuchsia-700 hover:to-pink-700 disabled:opacity-50 transition-all"
            >
              {loading ? 'Generating...' : 'Generate Video Prompts'}
            </button>
            {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
            {result && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700 space-y-1">
                <div><span className="font-semibold text-slate-900">Segments:</span> {result.segments.length}</div>
                <div><span className="font-semibold text-slate-900">Prompts Selected:</span> {selectedPromptCount} / {totalPromptCount}</div>
                {lastGeneratedAt && <div><span className="font-semibold text-slate-900">Generated:</span> {new Date(lastGeneratedAt).toLocaleTimeString()}</div>}
              </div>
            )}
            {result && (
              <div className="flex gap-2">
                <button
                  onClick={() => bulkSelect('all')}
                  className="flex-1 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
                >
                  Select All
                </button>
                <button
                  onClick={() => bulkSelect('none')}
                  className="flex-1 px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-300"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Narration Script</label>
            <textarea
              value={scriptInput}
              onChange={(e) => setScriptInput(e.target.value)}
              placeholder="Paste your narration script here..."
              rows={18}
              className="w-full h-full p-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">
              🔍 Product Search & Assignment
            </h3>
            <p className="text-slate-600 mb-6">
              Search for products from your Shopify store and assign them to each video segment. The AI will generate prompts featuring neutral, timeless interior styles (mid-century, farmhouse, rustic, wabi-sabi, Japanese, minimalist, Scandinavian) and intelligently place products only where they naturally enhance the visual storytelling.
            </p>

            {/* Search Bar */}
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
                placeholder="Search products (e.g., 'handwoven rug', 'brass lamp', 'velvet sofa')..."
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              <button
                onClick={searchProducts}
                disabled={loadingProducts || !searchQuery.trim()}
                className="px-6 py-3 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white font-semibold rounded-lg hover:from-fuchsia-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loadingProducts ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Product Results */}
            {products.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-slate-800">
                    Found {products.length} product{products.length !== 1 ? 's' : ''}
                    {selectedProducts.size > 0 && (
                      <span className="ml-2 text-pink-600">
                        ({selectedProducts.size} selected)
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2">
                    {selectedProducts.size > 0 && (
                      <>
                        <button
                          onClick={() => setSelectedProducts(new Set())}
                          className="text-sm text-slate-600 hover:text-slate-800 underline"
                        >
                          Clear Selection
                        </button>
                        <button
                          onClick={assignProductsToSegments}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 text-sm"
                        >
                          Auto-Assign to Segments
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`cursor-pointer border-2 rounded-lg overflow-hidden transition-all transform hover:scale-105 ${
                        selectedProducts.has(product.id)
                          ? 'border-pink-500 bg-pink-50 shadow-lg'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                      }`}
                    >
                      <div className="relative">
                        {product.images[0] && (
                          <img
                            src={product.images[0].url}
                            alt={product.images[0].alt}
                            className="w-full h-48 object-cover"
                          />
                        )}
                        {selectedProducts.has(product.id) && (
                          <div className="absolute top-2 right-2 bg-pink-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-lg">
                            ✓
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h5 className="font-semibold text-slate-800 mb-2 line-clamp-2">
                          {product.title}
                        </h5>
                        <p className="text-sm text-slate-600">{product.images.length} images</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {products.length === 0 && searchQuery && !loadingProducts && (
              <p className="text-sm text-slate-500 text-center py-4">No products found. Try a different search.</p>
            )}
          </div>

          {/* Segment Product Assignment */}
          {selectedProducts.size > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
              <h4 className="text-lg font-bold text-slate-800 mb-4">🎬 Assign Products to Segments</h4>
              <p className="text-slate-600 mb-6">
                Assign products to each video segment. The AI will intelligently decide where and when to include products in the prompts - only placing them where they naturally enhance the visual storytelling, not forcing them into every image.
              </p>

              <div className="space-y-4">
                {result.segments.map((segment) => (
                  <div key={segment.segmentOrder} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-sm uppercase tracking-wide text-pink-600 font-semibold">Segment {segment.segmentOrder}</span>
                        <h5 className="text-lg font-bold text-slate-800">{segment.segmentName || `Segment ${segment.segmentOrder}`}</h5>
                        {segment.narrationExcerpt && (
                          <p className="text-sm text-slate-600 mt-1">{segment.narrationExcerpt}</p>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">
                        {(segmentProducts[segment.segmentOrder] || []).length} product{(segmentProducts[segment.segmentOrder] || []).length !== 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Assigned Products */}
                    {(segmentProducts[segment.segmentOrder] || []).length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-sm font-semibold text-slate-700 mb-2">Assigned Products:</h6>
                        <div className="flex flex-wrap gap-2">
                          {(segmentProducts[segment.segmentOrder] || []).map((product) => (
                            <div key={product.id} className="bg-pink-50 border border-pink-200 rounded-lg p-2 flex items-center gap-2">
                              <img
                                src={product.images[0]?.url}
                                alt={product.title}
                                className="w-8 h-8 object-cover rounded"
                              />
                              <span className="text-xs font-medium text-slate-800">{product.title}</span>
                              <button
                                onClick={() => removeProductFromSegment(segment.segmentOrder, product.id)}
                                className="text-pink-600 hover:text-pink-800 text-xs"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Products to Assign */}
                    <div>
                      <h6 className="text-sm font-semibold text-slate-700 mb-2">Available Products:</h6>
                      <div className="flex flex-wrap gap-2">
                        {Array.from(selectedProducts).map((productId) => {
                          const product = products.find(p => p.id === productId);
                          if (!product) return null;
                          return (
                            <button
                              key={product.id}
                              onClick={() => assignProductToSegment(product.id, segment.segmentOrder)}
                              className="bg-slate-50 border border-slate-200 rounded-lg p-2 flex items-center gap-2 hover:bg-slate-100 transition-colors"
                            >
                              <img
                                src={product.images[0]?.url}
                                alt={product.title}
                                className="w-8 h-8 object-cover rounded"
                              />
                              <span className="text-xs font-medium text-slate-800">{product.title}</span>
                              <span className="text-xs text-slate-500">→ Assign</span>
                            </button>
                          );
                        })}
                      </div>
                      {selectedProducts.size === 0 && (
                        <p className="text-sm text-slate-500">No products selected above</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regenerate Button */}
          {Object.values(segmentProducts).some((prods) => prods.length > 0) && (
            <div className="flex justify-center">
              <button
                onClick={generateWithSegmentProducts}
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all text-lg"
              >
                {loading ? '✨ Regenerating Prompts...' : '✨ Regenerate Prompts (AI will intelligently place products)'}
              </button>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {result.narrationNotes && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
              <div className="font-semibold mb-1">Narration Notes</div>
              <p>{result.narrationNotes}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={copyAllPrompts}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              Copy Selected Prompts
            </button>
            <button
              onClick={copyJson}
              disabled={!exportJsonString}
              className="px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200 disabled:opacity-50"
            >
              Copy JSON for Video Creator
            </button>
            <button
              onClick={downloadJson}
              disabled={!exportJsonString}
              className="px-4 py-2 rounded-lg bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200 disabled:opacity-50"
            >
              Download JSON File
            </button>
          </div>

          <div className="space-y-6">
            {result.segments.map((segment) => {
              const chosen = selectedPrompts[segment.segmentOrder] || [];
              return (
                <div key={segment.segmentOrder} className="bg-white border border-slate-200 rounded-xl shadow-sm">
                  <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="text-sm uppercase tracking-wide text-pink-600 font-semibold">Segment {segment.segmentOrder}</div>
                      <h3 className="text-xl font-bold text-slate-800">{segment.segmentName || 'Untitled Segment'}</h3>
                      {segment.narrationExcerpt && (
                        <p className="text-sm text-slate-600 mt-2">{segment.narrationExcerpt}</p>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">
                      {chosen.length} selected • {segment.prompts.length} total
                    </div>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {segment.keyMoments && segment.keyMoments.length > 0 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Key Moments to Emphasize</div>
                        <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                          {segment.keyMoments.map((moment, idx) => (
                            <li key={idx}>{moment}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {segment.prompts.map((prompt) => {
                        const isSelected = chosen.includes(prompt.promptId);
                        return (
                          <div
                            key={prompt.promptId}
                            onClick={() => togglePromptSelection(segment.segmentOrder, prompt.promptId)}
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              isSelected ? 'border-pink-500 bg-pink-50 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-xs font-semibold text-slate-500 uppercase">Prompt</div>
                              {isSelected && <span className="text-xs bg-pink-600 text-white px-2 py-1 rounded-full">Selected</span>}
                            </div>
                            <p className="text-sm text-slate-800 mt-2">{prompt.promptText}</p>
                            <div className="mt-3 space-y-1 text-xs text-slate-600">
                              {prompt.visualGoal && <div><span className="font-semibold text-slate-700">Visual Goal:</span> {prompt.visualGoal}</div>}
                              {prompt.differentiation && <div><span className="font-semibold text-slate-700">Differentiation:</span> {prompt.differentiation}</div>}
                              {prompt.cameraDirection && <div><span className="font-semibold text-slate-700">Camera:</span> {prompt.cameraDirection}</div>}
                              {prompt.colorPalette && <div><span className="font-semibold text-slate-700">Palette:</span> {prompt.colorPalette}</div>}
                              {prompt.emotionalTone && <div><span className="font-semibold text-slate-700">Tone:</span> {prompt.emotionalTone}</div>}
                              {prompt.supportingNarration && <div><span className="font-semibold text-slate-700">Supports:</span> {prompt.supportingNarration}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {exportJsonString && (
            <div className="bg-slate-900 text-slate-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-white">Preview JSON</h4>
                <button
                  onClick={copyJson}
                  className="text-xs px-3 py-1 bg-white/10 text-white rounded-md hover:bg-white/20"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap max-h-64">{exportJsonString}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPromptGenerator;
